import React, { memo, useCallback, useMemo, useState } from 'react'
import CloseIcon from 'transfix-icons/build/CloseIcon'
import IconButton from 'transfix-ui/components/IconButton'
import SidePanel from 'transfix-ui/components/SidePanel'
import { makeStyles } from 'transfix-ui/core'
import { IBidRateCoachGrades } from 'types/graphqlTypes'
import CarrierOptionOverflowMenu from '../CarrierOptionOverflowMenu'
import CarrierOptionRateCard from '../CarrierOptionRateCard'
import { CounterOfferCard } from '../CarrierOptionRateCard/components/CounterOfferCard'
import { useShipmentRates } from '../CarrierOptionRateCard/hooks'
import ShipmentDetailsCard from '../ShipmentDetailsCard'
import CarrierOptionAcceptedCard from './components/CarrierOptionAcceptedCard/CarrierOptionAcceptedCard'
import CarrierOptionActivity from './components/CarrierOptionActivity/CarrierOptionActivity'
import CarrierOptionBouncedCard from './components/CarrierOptionBouncedCard'
import CarrierOptionButtons from './components/CarrierOptionButtons'
import CarrierOptionsContactsCard from './components/CarrierOptionsContactsCard'
import CarrierOptionsDetailsCard from './components/CarrierOptionsDetailsCard'
import RelatedOptionsCard from './components/RelatedOptionsCard/RelatedOptionsCard'
import { useCarrierOptionDetailsQuery } from './graphql/CarrierOptionSidePanelData'
import { getBouncedCarrierAssignment } from './utils'

export interface ICarrierOptionSidePanelProps {
  optionId?: string
  onClose: () => void
  /**
   * @default false
   */
  includeShipmentDetailsCard?: boolean
  onEditSuccess: () => void
}

const useCarrierOptionsSidePanelStyles = makeStyles(theme => ({
  sidePanel: {
    width: '100%',
    maxWidth: '500px',
  },
  menuBar: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: '2px',
  },
  sidePanelContainer: {
    '& > *': { marginTop: theme.spacing(2) }, // each child card
  },
}))

const CarrierOptionSidePanel = memo<ICarrierOptionSidePanelProps>(
  ({
    includeShipmentDetailsCard = false,
    onClose,
    onEditSuccess: onEditSuccessProp,
    optionId = '',
  }) => {
    const [carrierOptionId, setCarrierOptionId] = useState<string>(optionId)

    const classes = useCarrierOptionsSidePanelStyles()

    const { data, loading } = useCarrierOptionDetailsQuery({
      variables: { optionId: carrierOptionId },
    })
    const { bid } = data || {}
    const {
      acceptedAt: originalBidAcceptedAt,
      amount,
      carrierApplication,
      counterBid,
      id: parentBidId,
      latestBidApproval,
      relatedOptions,
      shipment,
      status: originalBidStatus,
    } = bid || {}
    const { id: carrierApplicationId } = carrierApplication || {}
    const { acceptedAt: counterBidAcceptedAt, status: counterBidStatus } = counterBid || {}
    const { bouncedCarrierAssignments, id: shipmentId } = shipment || {}

    const { rateCoachGrade } = useShipmentRates({ rate: amount, shipmentId })

    const isOriginalBidAccepted = originalBidStatus === 'Accepted'
    const isCounterBidAccepted = counterBidStatus === 'Accepted'

    const isOptionAccepted = isOriginalBidAccepted || isCounterBidAccepted
    const acceptedAt = isOriginalBidAccepted ? originalBidAcceptedAt : counterBidAcceptedAt

    const bouncedCarrierAssignment = getBouncedCarrierAssignment({
      bouncedCarrierAssignments,
      optionCarrierApplicationId: carrierApplicationId,
    })
    const { bouncedAt } = bouncedCarrierAssignment || {}
    const isCarrierBounced = Boolean(bouncedCarrierAssignment)

    const showCounterOfferCard = useMemo(() => {
      if (!rateCoachGrade) {
        return false
      }

      return [IBidRateCoachGrades.fair, IBidRateCoachGrades.poor].includes(rateCoachGrade)
    }, [rateCoachGrade])

    const onEditSuccess = useCallback(
      (optionId?: string) => {
        if (optionId) {
          setCarrierOptionId(optionId)
        }
        onEditSuccessProp()
      },
      [onEditSuccessProp]
    )

    return (
      <SidePanel
        title='Review option'
        anchor='right'
        onClose={onClose}
        PaperProps={{
          className: classes.sidePanel,
        }}
        menuItems={
          bid && (
            <div className={classes.menuBar}>
              <CarrierOptionOverflowMenu option={bid} onEditSuccess={onEditSuccess} />
              <IconButton
                Icon={CloseIcon}
                label='menu'
                onClick={onClose}
                color='tertiary'
                size='large'
              />
            </div>
          )
        }>
        <div className={classes.sidePanelContainer} data-test='option-side-panel'>
          {isCarrierBounced && <CarrierOptionBouncedCard bouncedAt={bouncedAt} />}
          {isOptionAccepted && acceptedAt && (
            <CarrierOptionAcceptedCard
              acceptedAt={acceptedAt}
              isCounterBidAccepted={isCounterBidAccepted}
            />
          )}
          {includeShipmentDetailsCard && <ShipmentDetailsCard shipmentId={shipmentId} compact />}

          <CarrierOptionRateCard
            shipmentId={shipmentId}
            carrierApplicationId={carrierApplicationId}
            rate={amount}
            counterOfferComponent={
              <>
                {showCounterOfferCard && parentBidId && (
                  <CounterOfferCard
                    parentBidId={parentBidId}
                    parentBidAmount={amount}
                    parentBidStatus={originalBidStatus}
                    counterBid={counterBid}
                  />
                )}
              </>
            }>
            <>
              {bid ? <CarrierOptionButtons option={bid} bidApproval={latestBidApproval} /> : null}
            </>
          </CarrierOptionRateCard>
          <CarrierOptionsContactsCard option={bid} loading={loading} />
          <CarrierOptionsDetailsCard option={bid} loading={loading} />
          <RelatedOptionsCard relatedOptions={relatedOptions} loading={loading} />
          <CarrierOptionActivity optionId={optionId} />
        </div>
      </SidePanel>
    )
  }
)

export default CarrierOptionSidePanel
